import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  model,
  OnDestroy,
  OnInit,
  output,
  Signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  TuiDataList,
  TuiIcon,
  TuiButton,
  TuiDropdown,
  TuiTextfield,
} from '@taiga-ui/core';
import { DeviceDetectorService } from '@core/services/device-detector';
import { IsLoadingService } from '@core/services';
import { RequestService } from '@core/services/request/request.service';
import { BehaviorSubject } from 'rxjs';
import { TuiDataListWrapper, TuiSelect, TuiChevron } from '@taiga-ui/kit';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'x-table-pagination',
  imports: [
    TuiTextfield,
    TuiChevron,
    CommonModule,
    TuiButton,
    TuiDataList,
    TuiIcon,
    TuiSelect,
    FormsModule,
    TuiDataListWrapper,
    TranslatePipe,
    TuiDropdown,
  ],
  templateUrl: './table-pagination.component.html',
  styleUrl: './table-pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TablePaginationComponent implements OnInit, OnDestroy {
  public readonly requestService = inject(RequestService);
  public readonly deviceService = inject(DeviceDetectorService);
  private cdr = inject(ChangeDetectorRef);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  public $currentPage = model<number>(1, { alias: 'currentPage' }); // Текущая страница
  public $limit = model<number>(10, { alias: 'limit' }); // Показать записей на странице
  public $total: Signal<number> | undefined; // Всего записей

  @ViewChild('limitValueContentTpl') limitValueContent!: TemplateRef<{
    $implicit: number;
  }>;
  @ViewChild('limitContentTpl') limitContent!: TemplateRef<{
    $implicit: number;
  }>;

  setPageEvent = output<number>();
  setLimitEvent = output<number>();

  public totalPages = 1; // Всего страниц
  public lastPage = false; // Последняя страница
  public showedOn = 1; // Показаны записи с
  public showedFor = 10; // Показаны записи по
  public pageAr: number[] = [];
  public overflow: {
    left: boolean;
    right: boolean;
  } = {
    left: false,
    right: false,
  };

  constructor() {
    effect(() => {
      this.$total!();
      this.$currentPage();
      this.$limit();
      if (this.$total!() !== 0) {
        this.getPaginatedUserRolesChanges();
      }
    });
  }

  ngOnInit(): void {
    this.$total = computed(() => this.requestService.loadedRequestsCount()!);
  }

  ngOnDestroy(): void {
    this.$total = undefined; // Удаляем сигнал, чтобы избежать утечек памяти
  }

  getPaginatedUserRolesChanges() {
    const limit = this.$limit();
    const total = this.$total!();

    this.totalPages = Math.ceil(total / limit);
    this.pageAr = [];

    let pageAr = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    let overflowLeft = false;
    let overflowRight = false;
    const pageArLn = pageAr.length;
    const currentPage = this.$currentPage();

    // Если в пагинации больше 10 страниц
    if (pageArLn > 10) {
      if (currentPage < 5) {
        // текущая от 0 до 5 режем массив от 0 до 10
        pageAr = pageAr.slice(0, 10);
        overflowRight = true;
      } else if (currentPage > pageArLn - 5) {
        // текущая является одной из 5ти последних, режем 10 от конца
        pageAr = pageAr.slice(pageArLn - 10);
        overflowLeft = true;
      } else {
        // все остальные случаи - режем 5 назад и 5 вперед
        pageAr = pageAr.slice(currentPage - 5, currentPage + 5);
        overflowLeft = true;
        overflowRight = true;
      }
    }
    this.pageAr = pageAr;

    this.lastPage = currentPage === this.totalPages;
    this.showedOn = limit * currentPage - limit + 1;
    this.showedFor = this.lastPage ? total : limit * currentPage;
    this.overflow = {
      left: overflowLeft,
      right: overflowRight,
    };
    this.cdr.markForCheck();
  }

  setLimit(limitParam?: number) {
    const limit = limitParam ?? this.$limit();

    if (limit === this.$limit()) {
      return;
    }
    this.$limit.set(limit);
    this.getPaginatedUserRolesChanges();
    this.setLimitEvent.emit(limit);
  }

  setPage(page: number) {
    this.$currentPage.set(page);
    this.getPaginatedUserRolesChanges();
    this.setPageEvent.emit(page);
  }

  prevPage() {
    const currentPage = this.$currentPage();

    if (currentPage > 1) {
      this.$currentPage.set(currentPage - 1);
      this.getPaginatedUserRolesChanges();
      this.setPageEvent.emit(this.$currentPage());
    }
  }

  nextPage() {
    const currentPage = this.$currentPage();

    if (currentPage < this.totalPages) {
      this.$currentPage.set(currentPage + 1);
      this.getPaginatedUserRolesChanges();
      this.setPageEvent.emit(this.$currentPage());
    }
  }
}
